import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate } from '../lib/utils';
import {
  Users, Plus, Edit2, Trash2, Phone, MapPin, GraduationCap, Shield, Wrench,
  TrendingUp, Star, Calendar, CheckCircle2, XCircle, Clock, Briefcase,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import ExportButton from '../components/ExportButton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'administrator', label: 'Administrators', icon: Shield, accent: 'bg-purple-100 text-purple-600' },
  { id: 'teaching', label: 'Teaching Staff', icon: GraduationCap, accent: 'bg-emerald-100 text-emerald-600' },
  { id: 'non_teaching', label: 'Non-Teaching Staff', icon: Wrench, accent: 'bg-amber-100 text-amber-600' },
];

const ATTENDANCE_STYLES = {
  present: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400',
  absent: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400',
  late: 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
  leave: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400',
};

const emptyForm = {
  name: '', category: 'administrator', phone: '', home_address: '',
  emergency_contact_name: '', emergency_contact_phone: '', salary: '',
  employment_status: 'active',
  class_taught: '', subject: '', qualification: '',
  title: '', admin_role: '', other_attributes: '',
  position: '', notes: '',
};

const PersonnelStaffPage = () => {
  const { hasRole } = useAuth();
  const canEdit = hasRole(['admin', 'school_head']);

  const [activeCat, setActiveCat] = useState('administrator');
  const [staff, setStaff] = useState([]);
  const [summary, setSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | on_leave
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  // Profile view state
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [profileTab, setProfileTab] = useState('overview');
  const [markAttendanceDate, setMarkAttendanceDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [reviewForm, setReviewForm] = useState({ review_date: format(new Date(), 'yyyy-MM-dd'), reviewer_name: '', score: '', strengths: '', areas_for_improvement: '', comments: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [listRes, sumRes] = await Promise.all([
        axios.get(`${API_URL}/api/personnel/staff`),
        axios.get(`${API_URL}/api/personnel/staff/summary`),
      ]);
      setStaff(listRes.data);
      setSummary(sumRes.data);
    } catch (e) {
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  const openProfile = async (s) => {
    setProfileOpen(true);
    setProfileData({ staff: s, loading: true });
    try {
      const res = await axios.get(`${API_URL}/api/personnel/staff/${s.id}/profile`);
      setProfileData({ ...res.data, loading: false });
    } catch { setProfileData(null); }
  };

  const refreshProfile = async () => {
    if (!profileData?.staff?.id) return;
    const res = await axios.get(`${API_URL}/api/personnel/staff/${profileData.staff.id}/profile`);
    setProfileData({ ...res.data, loading: false });
  };

  const markStaffAttendance = async (status) => {
    try {
      await axios.post(`${API_URL}/api/personnel/staff/attendance`, {
        personnel_staff_id: profileData.staff.id,
        date: markAttendanceDate,
        status,
      });
      toast.success('Attendance recorded');
      refreshProfile();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record');
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/personnel/staff/reviews`, {
        personnel_staff_id: profileData.staff.id,
        review_date: reviewForm.review_date,
        reviewer_name: reviewForm.reviewer_name || null,
        score: parseFloat(reviewForm.score),
        strengths: reviewForm.strengths || null,
        areas_for_improvement: reviewForm.areas_for_improvement || null,
        comments: reviewForm.comments || null,
      });
      toast.success('Review added');
      setReviewForm({ review_date: format(new Date(), 'yyyy-MM-dd'), reviewer_name: '', score: '', strengths: '', areas_for_improvement: '', comments: '' });
      refreshProfile();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add review');
    }
  };

  const deleteReview = async (rid) => {
    if (!window.confirm('Delete this review?')) return;
    await axios.delete(`${API_URL}/api/personnel/staff/reviews/${rid}`);
    refreshProfile();
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, category: activeCat });
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ ...emptyForm, ...s, salary: s.salary ?? '' });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, salary: form.salary ? parseFloat(form.salary) : 0 };
      if (editing) {
        await axios.put(`${API_URL}/api/personnel/staff/${editing.id}`, payload);
        toast.success('Staff updated');
      } else {
        await axios.post(`${API_URL}/api/personnel/staff`, payload);
        toast.success('Staff added');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ ...emptyForm });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/personnel/staff/${id}`);
      toast.success('Staff member deleted');
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const getInitials = (n) => n?.split(' ').map((x) => x[0]).join('').toUpperCase().slice(0, 2) || 'S';
  const filtered = staff.filter((s) => {
    if (s.category !== activeCat) return false;
    if (statusFilter !== 'all' && (s.employment_status || 'active') !== statusFilter) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-semibold">Staff Directory</h2>
          <p className="text-muted-foreground">Click any staff member to view their performance, attendance & review history</p>
        </div>
        <ExportButton path="exports/list/personnel-staff" filenameBase="suubi_staff" testId="staff-export-btn" />
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-primary/10"><Users className="h-6 w-6 text-primary" /></div>
            <div>
              <p className="text-sm text-muted-foreground">Total Staff</p>
              <p className="text-2xl font-heading font-bold">{summary?.total || 0}</p>
            </div>
          </CardContent>
        </Card>
        {CATEGORIES.map((c) => {
          const Icon = c.icon;
          return (
            <Card key={c.id} className="card-hover">
              <CardContent className="p-5 flex items-center gap-4">
                <div className={`p-3 rounded-xl ${c.accent}`}><Icon className="h-6 w-6" /></div>
                <div>
                  <p className="text-sm text-muted-foreground">{c.label}</p>
                  <p className="text-2xl font-heading font-bold">{summary?.by_category?.[c.id] || 0}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs value={activeCat} onValueChange={setActiveCat}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <TabsList>
            {CATEGORIES.map((c) => (
              <TabsTrigger key={c.id} value={c.id} data-testid={`tab-${c.id}`}>{c.label}</TabsTrigger>
            ))}
          </TabsList>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="staff-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="on_leave">On Leave</SelectItem>
              </SelectContent>
            </Select>
            {canEdit && (
              <Button onClick={openCreate} className="bg-primary" data-testid="add-staff-btn">
                <Plus className="mr-1 h-4 w-4" /> Add Staff
              </Button>
            )}
          </div>
        </div>

        {CATEGORIES.map((c) => (
          <TabsContent key={c.id} value={c.id} className="mt-6">
            {loading ? (
              <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        {c.id === 'teaching' && <TableHead>Subject / Class</TableHead>}
                        {c.id === 'administrator' && <TableHead>Title / Role</TableHead>}
                        {c.id === 'non_teaching' && <TableHead>Position</TableHead>}
                        <TableHead>Phone</TableHead>
                        <TableHead>Emergency Contact</TableHead>
                        {c.id === 'teaching' && <TableHead>Qualification</TableHead>}
                        <TableHead className="text-right">Salary</TableHead>
                        {canEdit && <TableHead className="w-28"></TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.length === 0 ? (
                        <TableRow><TableCell colSpan={canEdit ? 8 : 7} className="text-center py-10 text-muted-foreground">No {c.label.toLowerCase()} yet</TableCell></TableRow>
                      ) : (
                        filtered.map((s) => (
                          <TableRow key={s.id} className="table-row-hover" data-testid={`staff-row-${s.id}`}>
                            <TableCell>
                              <button className="flex items-center gap-3 hover:underline text-left" onClick={() => openProfile(s)} data-testid={`staff-profile-btn-${s.id}`}>
                                <Avatar className="h-9 w-9">
                                  <AvatarFallback className={c.accent}>{getInitials(s.name)}</AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-medium">{s.name}</p>
                                  <Badge variant="outline" className={(s.employment_status || 'active') === 'on_leave' ? 'mt-0.5 bg-amber-100 text-amber-700 border-amber-200 text-[10px]' : 'mt-0.5 bg-green-100 text-green-700 border-green-200 text-[10px]'}>
                                    {(s.employment_status || 'active') === 'on_leave' ? 'On Leave' : 'Active'}
                                  </Badge>
                                </div>
                              </button>
                            </TableCell>
                            {c.id === 'teaching' && (
                              <TableCell><div className="text-sm">{s.subject || '—'}{s.class_taught && <p className="text-xs text-muted-foreground">Class: {s.class_taught}</p>}</div></TableCell>
                            )}
                            {c.id === 'administrator' && (
                              <TableCell><div className="text-sm">{s.title || '—'}{s.admin_role && <p className="text-xs text-muted-foreground">{s.admin_role}</p>}</div></TableCell>
                            )}
                            {c.id === 'non_teaching' && <TableCell className="text-sm">{s.position || '—'}</TableCell>}
                            <TableCell className="text-sm">{s.phone || '—'}</TableCell>
                            <TableCell className="text-sm">
                              {s.emergency_contact_name ? (
                                <div>
                                  <p>{s.emergency_contact_name}</p>
                                  {s.emergency_contact_phone && <p className="text-xs text-muted-foreground">{s.emergency_contact_phone}</p>}
                                </div>
                              ) : '—'}
                            </TableCell>
                            {c.id === 'teaching' && <TableCell className="text-sm">{s.qualification || '—'}</TableCell>}
                            <TableCell className="text-right font-medium">{s.salary > 0 ? formatCurrency(s.salary) : '—'}</TableCell>
                            {canEdit && (
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1.5">
                                  <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`edit-staff-${s.id}`} className="h-8 px-2.5">
                                    <Edit2 className="h-3.5 w-3.5 mr-1" />
                                    Edit
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => setDeleteTarget(s)} data-testid={`delete-staff-${s.id}`} className="h-8 px-2.5 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive">
                                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                                    Delete
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditing(null); setForm({ ...emptyForm }); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Edit Staff' : 'Add Staff Member'}</DialogTitle>
            <DialogDescription>Record full details for this staff member</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="staff-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="staff-name-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Salary (UGX)</Label>
                <Input type="number" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Employment Status</Label>
                <Select value={form.employment_status} onValueChange={(v) => setForm({ ...form, employment_status: v })}>
                  <SelectTrigger data-testid="staff-employment-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="on_leave">On Leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Home Address</Label>
                <Input value={form.home_address} onChange={(e) => setForm({ ...form, home_address: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Emergency Contact Name</Label>
                <Input value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Emergency Contact Phone</Label>
                <Input value={form.emergency_contact_phone} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} />
              </div>
            </div>
            {form.category === 'teaching' && (
              <div className="space-y-4 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/10">
                <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">Teaching Staff Details</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Class Taught</Label><Input value={form.class_taught} onChange={(e) => setForm({ ...form, class_taught: e.target.value })} placeholder="e.g., P5" /></div>
                  <div className="space-y-2"><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
                </div>
                <div className="space-y-2"><Label>Level of Qualification</Label><Input value={form.qualification} onChange={(e) => setForm({ ...form, qualification: e.target.value })} /></div>
              </div>
            )}
            {form.category === 'administrator' && (
              <div className="space-y-4 p-4 rounded-lg bg-purple-50 dark:bg-purple-900/10">
                <p className="text-sm font-medium text-purple-700 dark:text-purple-400">Administrator Details</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Role</Label><Input value={form.admin_role} onChange={(e) => setForm({ ...form, admin_role: e.target.value })} /></div>
                </div>
                <div className="space-y-2"><Label>Other (custom attributes)</Label><Textarea value={form.other_attributes} onChange={(e) => setForm({ ...form, other_attributes: e.target.value })} rows={2} /></div>
              </div>
            )}
            {form.category === 'non_teaching' && (
              <div className="space-y-2 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/10">
                <Label>Position</Label>
                <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="e.g., Cook, Security" />
              </div>
            )}
            <div className="space-y-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="staff-submit-btn">{editing ? 'Update' : 'Add'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Staff Profile Dialog (Performance + Attendance + Reviews integrated) */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="staff-profile-dialog">
          {!profileData || profileData.loading ? (
            <div className="py-12 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
          ) : (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback className="bg-primary/10 text-primary">{getInitials(profileData.staff.name)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <DialogTitle className="font-heading">{profileData.staff.name}</DialogTitle>
                    <DialogDescription className="capitalize">
                      {profileData.staff.category?.replace(/_/g, ' ')}
                      {profileData.staff.subject && ` · ${profileData.staff.subject}`}
                      {profileData.staff.title && ` · ${profileData.staff.title}`}
                      {profileData.staff.position && ` · ${profileData.staff.position}`}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <Tabs value={profileTab} onValueChange={setProfileTab}>
                <TabsList>
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="attendance" data-testid="profile-tab-attendance">Attendance</TabsTrigger>
                  <TabsTrigger value="reviews" data-testid="profile-tab-reviews">Performance Reviews</TabsTrigger>
                </TabsList>

                {/* Overview */}
                <TabsContent value="overview" className="mt-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 rounded-xl border">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground"><TrendingUp className="h-4 w-4" /> Avg Review Score</div>
                      <p className="text-3xl font-heading font-bold text-primary mt-1">{profileData.review_stats.average_score}/10</p>
                      <Progress value={profileData.review_stats.average_score * 10} className="mt-2 h-1.5" />
                      <p className="text-xs text-muted-foreground mt-1">{profileData.review_stats.total_reviews} review{profileData.review_stats.total_reviews === 1 ? '' : 's'}</p>
                    </div>
                    <div className="p-4 rounded-xl border">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground"><Calendar className="h-4 w-4" /> Attendance Rate</div>
                      <p className="text-3xl font-heading font-bold text-green-600 mt-1">{profileData.attendance_stats.attendance_rate}%</p>
                      <Progress value={profileData.attendance_stats.attendance_rate} className="mt-2 h-1.5" />
                      <p className="text-xs text-muted-foreground mt-1">{profileData.attendance_stats.total_days} recorded days</p>
                    </div>
                    <div className="p-4 rounded-xl border">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground"><Briefcase className="h-4 w-4" /> Contact</div>
                      <p className="text-sm mt-2">{profileData.staff.phone || '—'}</p>
                      <p className="text-xs text-muted-foreground">{profileData.staff.home_address || 'No address'}</p>
                    </div>
                  </div>
                  {profileData.review_stats.latest_review && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2"><Star className="h-4 w-4 text-amber-500" /> Latest Review</CardTitle>
                        <CardDescription>{formatDate(profileData.review_stats.latest_review.review_date)} by {profileData.review_stats.latest_review.reviewer_name || 'Reviewer'}</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-primary/10 text-primary">Score: {profileData.review_stats.latest_review.score}/10</Badge>
                        </div>
                        {profileData.review_stats.latest_review.strengths && (<p><span className="font-medium">Strengths:</span> {profileData.review_stats.latest_review.strengths}</p>)}
                        {profileData.review_stats.latest_review.areas_for_improvement && (<p><span className="font-medium">Areas to Improve:</span> {profileData.review_stats.latest_review.areas_for_improvement}</p>)}
                        {profileData.review_stats.latest_review.comments && (<p className="text-muted-foreground">{profileData.review_stats.latest_review.comments}</p>)}
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                {/* Attendance */}
                <TabsContent value="attendance" className="mt-4 space-y-4">
                  {canEdit && (
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-end gap-3 flex-wrap">
                          <div className="space-y-1.5">
                            <Label className="text-xs">Date</Label>
                            <Input type="date" value={markAttendanceDate} onChange={(e) => setMarkAttendanceDate(e.target.value)} className="w-40" />
                          </div>
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => markStaffAttendance('present')} data-testid="mark-staff-present"><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Present</Button>
                          <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => markStaffAttendance('absent')}><XCircle className="h-3.5 w-3.5 mr-1" /> Absent</Button>
                          <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => markStaffAttendance('late')}><Clock className="h-3.5 w-3.5 mr-1" /> Late</Button>
                          <Button size="sm" variant="outline" onClick={() => markStaffAttendance('leave')}>Leave</Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  <div className="grid grid-cols-4 gap-3">
                    <div className="p-3 rounded-lg border text-center"><p className="text-xs text-muted-foreground">Present</p><p className="text-xl font-bold text-green-600">{profileData.attendance_stats.present}</p></div>
                    <div className="p-3 rounded-lg border text-center"><p className="text-xs text-muted-foreground">Absent</p><p className="text-xl font-bold text-red-600">{profileData.attendance_stats.absent}</p></div>
                    <div className="p-3 rounded-lg border text-center"><p className="text-xs text-muted-foreground">Late</p><p className="text-xl font-bold text-amber-600">{profileData.attendance_stats.late}</p></div>
                    <div className="p-3 rounded-lg border text-center"><p className="text-xs text-muted-foreground">Leave</p><p className="text-xl font-bold text-blue-600">{profileData.attendance_stats.leave}</p></div>
                  </div>
                  <Card>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead>Notes</TableHead></TableRow></TableHeader>
                        <TableBody>
                          {profileData.attendance.length === 0 ? (
                            <TableRow><TableCell colSpan={3} className="text-center py-6 text-muted-foreground">No attendance records yet</TableCell></TableRow>
                          ) : profileData.attendance.map((a) => (
                            <TableRow key={a.id}>
                              <TableCell>{a.date}</TableCell>
                              <TableCell><Badge className={ATTENDANCE_STYLES[a.status]}>{a.status}</Badge></TableCell>
                              <TableCell className="text-muted-foreground">{a.notes || '—'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Reviews */}
                <TabsContent value="reviews" className="mt-4 space-y-4">
                  {canEdit && (
                    <Card>
                      <CardHeader className="pb-3"><CardTitle className="text-base">Add Review</CardTitle></CardHeader>
                      <CardContent>
                        <form onSubmit={submitReview} className="space-y-3">
                          <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1.5"><Label className="text-xs">Date</Label><Input type="date" value={reviewForm.review_date} onChange={(e) => setReviewForm({ ...reviewForm, review_date: e.target.value })} required /></div>
                            <div className="space-y-1.5"><Label className="text-xs">Reviewer</Label><Input value={reviewForm.reviewer_name} onChange={(e) => setReviewForm({ ...reviewForm, reviewer_name: e.target.value })} /></div>
                            <div className="space-y-1.5"><Label className="text-xs">Score (0-10) *</Label><Input type="number" min="0" max="10" step="0.1" value={reviewForm.score} onChange={(e) => setReviewForm({ ...reviewForm, score: e.target.value })} required data-testid="review-score-input" /></div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label className="text-xs">Strengths</Label><Textarea rows={2} value={reviewForm.strengths} onChange={(e) => setReviewForm({ ...reviewForm, strengths: e.target.value })} /></div>
                            <div className="space-y-1.5"><Label className="text-xs">Areas to Improve</Label><Textarea rows={2} value={reviewForm.areas_for_improvement} onChange={(e) => setReviewForm({ ...reviewForm, areas_for_improvement: e.target.value })} /></div>
                          </div>
                          <div className="space-y-1.5"><Label className="text-xs">Comments</Label><Textarea rows={2} value={reviewForm.comments} onChange={(e) => setReviewForm({ ...reviewForm, comments: e.target.value })} /></div>
                          <Button type="submit" size="sm" data-testid="submit-review-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add Review</Button>
                        </form>
                      </CardContent>
                    </Card>
                  )}
                  <div className="space-y-3">
                    {profileData.reviews.length === 0 ? (
                      <p className="text-center py-6 text-muted-foreground text-sm">No reviews yet</p>
                    ) : profileData.reviews.map((r) => (
                      <Card key={r.id}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className="bg-primary/10 text-primary">{r.score}/10</Badge>
                                <span className="text-sm text-muted-foreground">{formatDate(r.review_date)}</span>
                                {r.reviewer_name && <span className="text-xs text-muted-foreground">· by {r.reviewer_name}</span>}
                              </div>
                              {r.strengths && <p className="text-sm"><span className="font-medium">Strengths:</span> {r.strengths}</p>}
                              {r.areas_for_improvement && <p className="text-sm"><span className="font-medium">Improve:</span> {r.areas_for_improvement}</p>}
                              {r.comments && <p className="text-sm text-muted-foreground mt-1">{r.comments}</p>}
                            </div>
                            {canEdit && (
                              <Button size="icon" variant="ghost" className="text-destructive" onClick={() => deleteReview(r.id)}><Trash2 className="h-4 w-4" /></Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-staff-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete staff member?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget ? (
                <>Delete <strong>{deleteTarget.full_name}</strong> ({deleteTarget.position || deleteTarget.category})? This action cannot be undone.</>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && handleDelete(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-staff-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PersonnelStaffPage;
